import { useEffect, useState } from "react"
import { fetchAlertRule, updateAlertRule, clearAlertRule } from "../../lib/api"
import ThresholdEditor, { formatCount } from "./ThresholdEditor"

// Email alerts for one code or one group (#304, spec D3). `target` is
// { token } for a code or { groupId } for a group.
//
// A level with no targets of its own uses the next level's (code, then group,
// then the account default in Profile), and says where they come from, so an
// empty field never reads as "no alerts" when alerts are on.
//
// Renders nothing while the scan-target email is off for this account: a
// setting for an email that never sends is worse than no setting.
// `wrap` puts card chrome around the editor, so a caller gets no empty card
// when the editor has nothing to show.
export default function AlertRuleEditor({ target, wrap = (content) => content }) {
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    fetchAlertRule(target)
      .then((payload) => { if (!cancelled) setData(payload) })
      .catch(() => { /* No section. The rest of the page still works. */ })
    return () => { cancelled = true }
  }, [target.token, target.groupId])

  const features = data?.features || {}
  if (!data || (!features.scan_alert_rules && !features.quiet_alerts)) return null

  const own = data.rule?.scan_alert_thresholds ?? null
  const effective = data.effective

  async function save(change) {
    setBusy(true)
    setError(null)
    try {
      setData(await change())
    } catch (e) {
      setError(e.message || "Couldn't save that. Try again.")
    } finally {
      setBusy(false)
    }
  }

  const inherited = inheritedLabel(effective.thresholds_from, data.group, !!target.groupId)

  // The mute stores only a difference from the next level: matching the
  // inherited value clears this level's own setting, so a code follows its
  // group again when the owner puts it back.
  function setMuted(next) {
    const value = next === !!data.inherited?.quiet_alerts_muted ? null : next
    save(() => updateAlertRule(target, { quiet_alerts_muted: value }))
  }

  const muteId = target.groupId ? `group-${target.groupId}-mute` : `code-${target.token}-mute`

  return wrap(
    <div>
      {features.scan_alert_rules && (own === null ? (
        <div>
          <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light">
            {effective.scan_alert_thresholds.length > 0
              ? <>Uses {inherited}: <span className="font-mono text-ink dark:text-cloud">{effective.scan_alert_thresholds.map(formatCount).join(", ")}</span>.</>
              : emptyInherited(effective.thresholds_from, data.group, !!target.groupId)}
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => save(() => updateAlertRule(target, { scan_alert_thresholds: effective.scan_alert_thresholds }))}
            className="mt-2 text-sm font-semibold text-voltage-dark dark:text-voltage-light hover:underline bg-transparent border-0 p-0 cursor-pointer disabled:opacity-50"
          >
            Set targets for this {target.groupId ? "group" : "code"}
          </button>
        </div>
      ) : (
        <div>
          <ThresholdEditor
            id={target.groupId ? `group-${target.groupId}-targets` : `code-${target.token}-targets`}
            values={own}
            disabled={busy}
            onChange={(values) => save(() => updateAlertRule(target, { scan_alert_thresholds: values }))}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => save(() => data.rule?.quiet_alerts_muted == null
              ? clearAlertRule(target)
              : updateAlertRule(target, { scan_alert_thresholds: null }))}
            className="mt-2 text-xs text-[color:var(--color-muted)] dark:text-muted-light hover:text-ink dark:hover:text-cloud hover:underline bg-transparent border-0 p-0 cursor-pointer disabled:opacity-50"
          >
            Use {inheritedLabel(data.inherited?.thresholds_from, data.group, !!target.groupId)} instead
          </button>
        </div>
      ))}
      {features.quiet_alerts && (
        <label htmlFor={muteId} className={"flex items-start gap-2 text-sm text-ink dark:text-cloud cursor-pointer" + (features.scan_alert_rules ? " mt-3" : "")}>
          <input
            id={muteId}
            type="checkbox"
            checked={!!effective.quiet_alerts_muted}
            disabled={busy}
            onChange={(e) => setMuted(e.target.checked)}
            className="mt-0.5 h-4 w-4 flex-shrink-0 accent-[color:var(--color-voltage)] cursor-pointer disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"
          />
          <span>
            Mute no-scan alerts for this {target.groupId ? "group" : "code"}
            {effective.muted_from === "group" && !target.groupId && data.group && (
              <span className="block text-xs text-[color:var(--color-muted)] dark:text-muted-light">Muted with the group {data.group.name}.</span>
            )}
          </span>
        </label>
      )}
      {error && <p role="alert" className="text-xs text-fail mt-1.5">{error}</p>}
    </div>
  )
}

function emptyInherited(from, group, isGroup) {
  if (from === "group" && group && !isGroup) return `No scan targets. The group ${group.name} has none.`
  return "No scan targets. Your account default in Profile has none."
}

function inheritedLabel(from, group, isGroup) {
  if (from === "group" && group && !isGroup) return `the targets of the group ${group.name}`
  return "your account default"
}
